import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../../../../../colorVars';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import {
  // isBeforeNineThirty,
  isBeforeStartTime,
  validateTimeStamp,
} from '../../timestampFilter/timeStampFilter';
import Candle from './Candle';
import { candleNumberProcessor } from './utils/candleNumberProcessor';
import { checkIfNewCandle } from './utils/checkIfNewCandle';
import { createNewCandleTimeStamp } from './utils/createNewCandleTimeStamp';
import { generateInitialTimeStamp } from './utils/generateInitialNextTimeStamp';
export interface Candle {
  timeStamp: string;
}
interface Ref {
  current: any;
}
interface ZoomState {
  k: any
}
interface Props {
  currentCandle: Candle;
  caliber: number;
  scaleX: (value: number) => number;
  scaleY:(value: number) => number;
  hash: string;
  loading:boolean;
  currentZoomState: ZoomState;
  portalRef: Ref;
  size:number;
  secondToLast: Candle;
  numberOfCandles: number;
  updateOldCandles:(value: any) => void;
  millisBetweenCandles: number;
  priceMinimum: number;
  priceMaximum: number;
  setPriceMinimum:(value: number) => void;
  setPriceMaximum: (value: number) => void;
  dataSource: string;
  showCandleNumbers: boolean;
  startHour: string | number;
  startMin: string | number;
  timeUnit: string;
  candleDuration: number;
}
export const CurrentCandle: React.FC<Props> = observer(
  ({
    currentCandle,
    caliber,
    scaleX,
    scaleY,
    hash,
    loading,
    currentZoomState,
    portalRef,
    size,
    secondToLast,
    numberOfCandles,
    updateOldCandles,
    millisBetweenCandles,
    priceMinimum,
    priceMaximum,
    setPriceMinimum,
    setPriceMaximum,
    dataSource,
    showCandleNumbers,
    startHour,
    startMin,
    timeUnit,
    candleDuration,
  }) => {
    const [processedCandle, setProcessedCandle] = useState(null);
    const [unprocessedCandle, setUnprocessedCandle] = useState(null);
    const nextCandleTimeStampRef = useRef(null);
    const { priceDataStore } = useWebsocketStore();
    const price = priceDataStore.trades[hash]?.price ?? null;
    const currentTimeStamp = priceDataStore.trades[hash]?.timeStamp ?? null;

    // USE EFFECT RUNS WHEN COMPONENT RECEIVES A NEW CURRENT CANDLE FROM PARENT
    useEffect(() => {
      if (currentCandle && millisBetweenCandles) {
        setUnprocessedCandle(currentCandle);
        const [isBefore, startTimeStamp] = isBeforeStartTime(
          dataSource,
          startHour,
          startMin,
          currentCandle.timeStamp,
          timeUnit
        );
        if (isBefore) {
          nextCandleTimeStampRef.current = startTimeStamp;
        } else {
          const newTimeStamp = generateInitialTimeStamp({
            timeUnit,
            candleDuration,
            millisBetweenCandles,
            currentCandle,
          });
          nextCandleTimeStampRef.current = newTimeStamp;
        }
      }
    }, [currentCandle]);

    // potential fixes for candle skipping. Move lines 60+61 into check Fn. Replace lastCandleTimeStamp state with a ref so change is immediate.
    //RUNS EACH TIME PRICE CHANGES
    useEffect(() => {
      if (price !== null && !loading && millisBetweenCandles && unprocessedCandle) {
        const currentPrice = Number(price);
        const currentCandleTimeStamp = Number(currentTimeStamp);
        if (validateTimeStamp(dataSource, currentTimeStamp)) {
          if (
            checkIfNewCandle({
              currentCandleTimeStamp,
              nextCandleTimeStampRef,
            })
          ) {
            const newTimeStamp = createNewCandleTimeStamp(
              nextCandleTimeStampRef,
              millisBetweenCandles,
              timeUnit,
              candleDuration
            );
            updateOldCandles({ currentCandle: unprocessedCandle });
            setUnprocessedCandle((prev) => ({
              ...prev,
              high: currentPrice,
              low: currentPrice,
              close: currentPrice,
              open: currentPrice,
              timeStamp: newTimeStamp,
            }));
          } else {
            if (currentPrice < priceMinimum) setPriceMinimum(currentPrice);
            if (currentPrice > priceMaximum) setPriceMaximum(currentPrice);
            const candleToUpdate = { ...unprocessedCandle };
            candleToUpdate.close = currentPrice;
            if (currentPrice > unprocessedCandle?.high)
              candleToUpdate.high = currentPrice;
            if (currentPrice < unprocessedCandle?.low)
              candleToUpdate.low = currentPrice;
            setUnprocessedCandle(candleToUpdate);
          }
        }
      }
    }, [price, currentTimeStamp, loading]);

    //USE EFFECT RUNS WHEN UNPROCESSED CANDLE CHANGES OR ANY OF THE SCALES CHANGE
    useEffect(() => {
      if (
        unprocessedCandle !== null &&
        scaleY !== null &&
        scaleX !== null &&
        caliber !== null
      ) {
        const candle = {
          zoomMult: currentZoomState === null ? 1 : currentZoomState.k,
          candleNumber: candleNumberProcessor(secondToLast, unprocessedCandle),
          rectWidth: caliber,
          rectHeight:
            scaleY(Math.min(unprocessedCandle.open, unprocessedCandle.close)) -
            scaleY(Math.max(unprocessedCandle.open, unprocessedCandle.close)),
          rectYPos: scaleY(
            Math.max(unprocessedCandle.open, unprocessedCandle.close)
          ),
          rectXPos: scaleX(numberOfCandles - 1),
          fill:
            unprocessedCandle.open < unprocessedCandle.close
              ? colors.green
              : colors.red,
          lineHeight:
            scaleY(unprocessedCandle.low) - scaleY(unprocessedCandle.high),
          lineYPos: scaleY(unprocessedCandle.high),
          lineXPos: scaleX(numberOfCandles - 1) + caliber / 2,
          timeStamp: unprocessedCandle.timeStamp,
          candleOpen: unprocessedCandle.open,
          candleClose: unprocessedCandle.close,
          candleHigh: unprocessedCandle.high,
          candleLow: unprocessedCandle.low,
        };
        setProcessedCandle(candle);
      }
    }, [unprocessedCandle, caliber, scaleX, scaleY]);

    return (
      <>
        {processedCandle ? (
          <Candle
            candle={processedCandle}
            portalRef={portalRef}
            size={size}
            showCandleNumbers={showCandleNumbers}
            timeUnit={timeUnit}
          />
        ) : null}
      </>
    );
  }
);
